import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Input, Select } from '@/components/ui/Field';
import { Button } from '@/components/ui/Button';
import { Banner, Skeleton } from '@/components/ui/Feedback';
import { PageHeader } from '@/app/PlaceholderPage';
import { api, ApiError } from '@/lib/api';
import { databaseApi } from './api';
import { RegionPicker } from './RegionPicker';

interface FormState {
  name: string;
  regionId: string | null;
  angkatan: string;
  joinedPeriod: string;
  socialMedia: string;
  photoKey: string | null;
  status: string;
}

const EMPTY: FormState = {
  name: '',
  regionId: null,
  angkatan: '',
  joinedPeriod: '',
  socialMedia: '',
  photoKey: null,
  status: 'active',
};

export function TbmFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const { data: existing, isLoading } = useQuery({
    queryKey: ['tbm', id],
    queryFn: () => databaseApi.getTbm(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!existing) return;
    setForm({
      name: existing.name,
      regionId: existing.regionId,
      angkatan: existing.angkatan ?? '',
      joinedPeriod: existing.joinedPeriod ?? '',
      socialMedia: existing.socialMedia ?? '',
      photoKey: existing.photoKey ?? null,
      status: existing.status,
    });
  }, [existing]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setFieldErrors((e) => ({ ...e, [key]: '' }));
  };

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      isEdit ? databaseApi.updateTbm(id!, payload) : databaseApi.createTbm(payload),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['tbm'] });
      navigate(`/database/tbm/${isEdit ? id : (saved as { id: string }).id}`);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setFieldErrors(err.fields ?? {});
        setBanner(err.fields ? null : err.message);
      } else {
        setBanner('Terjadi kesalahan. Silakan coba lagi.');
      }
    },
  });

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setBanner('Ukuran berkas logo maksimal 5 MB.');
      return;
    }

    setUploadingLogo(true);
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('entityType', 'tbm_logo');
      body.append('entityId', id ?? 'new');

      const res = await api.post<{ id: string }>('/files', body);
      set('photoKey', res.id);
      setBanner(null);
    } catch (err) {
      setBanner(err instanceof ApiError ? err.message : 'Gagal mengunggah foto/logo.');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setBanner(null);

    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = 'Nama TBM wajib diisi';
    if (!form.regionId) errors.regionId = 'Desa wajib dipilih';

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setBanner('Beberapa isian wajib belum lengkap.');
      return;
    }

    // `code`, `id`, `created_at`, `updated_at` dikelola otomatis oleh server.
    mutation.mutate({
      name: form.name.trim(),
      regionId: form.regionId,
      angkatan: form.angkatan.trim() || null,
      joinedPeriod: form.joinedPeriod.trim() || null,
      socialMedia: form.socialMedia.trim() || null,
      photoKey: form.photoKey || null,
      status: form.status,
    });
  }

  if (isEdit && isLoading) return <Skeleton className="h-96 w-full" />;

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-5">
      <Link
        to={isEdit ? `/database/tbm/${id}` : '/database/tbm'}
        className="text-sm text-ink-secondary hover:text-primary"
      >
        ← Batal
      </Link>

      <PageHeader title={isEdit ? 'Ubah Profil TBM' : 'TBM Baru'} />

      {banner && <Banner tone={banner.startsWith('Ukuran') || banner.startsWith('Gagal') ? 'danger' : 'danger'}>{banner}</Banner>}

      <Card>
        <h2 className="mb-4 text-[17px] font-semibold">Identitas & Keikutsertaan</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Nama TBM"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            error={fieldErrors.name}
            required
          />
          <Input
            label="Kode TBM"
            value={isEdit ? (existing?.code ?? '') : 'dibuat otomatis setelah disimpan'}
            readOnly
            disabled
            hint="Nomor urut diterbitkan sistem dan tidak bisa diubah."
          />
          <Input
            label="Angkatan"
            value={form.angkatan}
            onChange={(e) => set('angkatan', e.target.value)}
            placeholder="Misal: Angkatan 1"
            hint="Kelompok angkatan pembinaan SLI"
          />
          <Input
            label="Bulan & Tahun Bergabung"
            type="month"
            value={form.joinedPeriod}
            onChange={(e) => set('joinedPeriod', e.target.value)}
            hint="Format: YYYY-MM (misal: 2024-01)"
          />
        </div>
      </Card>

      <Card>
        <h2 className="mb-4 text-[17px] font-semibold">Foto / Logo TBM</h2>
        <div className="flex flex-wrap items-center gap-4">
          {form.photoKey ? (
            <img
              src={`/api/v1/files/${form.photoKey}`}
              alt="Logo TBM"
              className="size-20 rounded-xl object-cover border border-border shadow-sm"
            />
          ) : (
            <div className="grid size-20 place-items-center rounded-xl bg-app text-2xl border border-dashed border-border text-ink-muted">
              📚
            </div>
          )}
          <div>
            <label className="inline-flex cursor-pointer items-center justify-center rounded-[var(--radius-button)] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover transition-colors">
              <span>{uploadingLogo ? 'Mengunggah…' : form.photoKey ? 'Ganti Foto/Logo' : 'Unggah Foto/Logo'}</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleLogoUpload}
                disabled={uploadingLogo}
              />
            </label>
            {form.photoKey && (
              <button
                type="button"
                onClick={() => set('photoKey', null)}
                className="ml-3 text-sm text-danger hover:underline cursor-pointer"
              >
                Hapus Foto
              </button>
            )}
            <p className="mt-1 text-[12px] text-ink-muted">JPG, PNG, atau WebP · maksimal 5 MB</p>
          </div>
        </div>
      </Card>

      <Card>
        <h2 className="mb-4 text-[17px] font-semibold">Lokasi</h2>
        <RegionPicker
          value={form.regionId}
          onChange={(v) => set('regionId', v)}
          error={fieldErrors.regionId}
          required
        />
      </Card>

      <Card>
        <h2 className="mb-4 text-[17px] font-semibold">Kontak & Status</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Media sosial"
            value={form.socialMedia}
            onChange={(e) => set('socialMedia', e.target.value)}
            hint="Mis. @tbmcahayailmu atau tautan Facebook"
          />
          <Select
            label="Status keikutsertaan"
            value={form.status}
            onChange={(e) => set('status', e.target.value)}
          >
            <option value="active">Aktif</option>
            <option value="inactive">Nonaktif</option>
            <option value="graduated">Lulus</option>
          </Select>
        </div>
      </Card>

      <div className="flex flex-wrap gap-3">
        <Button type="submit" loading={mutation.isPending}>
          {isEdit ? 'Simpan Perubahan' : 'Simpan TBM'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate(isEdit ? `/database/tbm/${id}` : '/database/tbm')}
        >
          Batal
        </Button>
      </div>
    </form>
  );
}
